import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Loader2, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  StepAddress,
  StepCalculator,
  StepEmployment,
  StepFinalReview,
  StepLoanType,
  StepPersonalDetails,
  useOnboardingController,
} from './onboarding-steps';

const Onboarding = () => {
  const {
    applicationId,
    approvalResult,
    canProceed,
    contactSubmitted,
    contactSubmitting,
    encouragement,
    formData,
    formatCurrency,
    handleAssetChange,
    handleBack,
    handleChange,
    handleLiabilityChange,
    handleNext,
    handleNoOtherDebtsChange,
    handleSpeakWithUs,
    handleSubmitApplication,
    loanAmount,
    loading,
    progress,
    returnHome,
    scoringError,
    scoringErrorMessage,
    scoringInProgress,
    setShowSpeakWithUs,
    setStep,
    showSpeakWithUs,
    step,
    totalSteps,
  } = useOnboardingController();

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="p-4 md:p-6">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <img
            src="/levr-logo.png"
            alt="Levr"
            className="w-28 md:w-36 h-auto object-contain"
          />
          {/* Prominent Progress Badge */}
          <div className="flex items-center gap-3">
            <div className="bg-[#00D47E] text-black px-4 py-2 rounded-full font-bold text-lg">
              {Math.round(progress)}%
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar - More Prominent */}
      <div className="px-6 pb-4">
        <div className="max-w-2xl mx-auto">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/60 text-sm">Step {step} of {totalSteps}</span>
            <span className="text-[#00D47E] text-sm font-medium">{Math.round(progress)}% complete</span>
          </div>
          {/* Thicker progress bar with glow effect */}
          <div className="relative">
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#00D47E] to-[#00FF94] rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            {/* Glow effect */}
            <div
              className="absolute top-0 h-3 bg-[#00D47E]/30 rounded-full blur-sm"
              style={{ width: `${progress}%` }}
            />
          </div>
          {/* Step dots — tap to jump back to completed steps */}
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => {
              // Locked from the moment submission STARTS, not just once the result lands. That
              // POST is the single durable write: if someone navigates back and edits while it's
              // in flight, the response arrives describing answers that are no longer on screen.
              // Afterwards the funnel is simply finished — to change anything they start again,
              // which the backend treats as a genuine second attempt.
              const isLocked = Boolean(approvalResult) || contactSubmitted || loading || scoringInProgress;
              const isCompleted = s < step && !isLocked;
              const isCurrent = s === step;
              return (
                <button
                  key={s}
                  onClick={() => isCompleted && setStep(s)}
                  disabled={!isCompleted}
                  aria-label={`Step ${s}${isCompleted ? ' (completed, click to go back)' : isCurrent ? ' (current)' : ''}`}
                  className={`rounded-full transition-all ${
                    isCurrent
                      ? 'w-6 h-2.5 bg-[#00D47E]'
                      : isCompleted
                        ? 'w-2.5 h-2.5 bg-[#00D47E]/60 hover:bg-[#00D47E] cursor-pointer'
                        : 'w-2.5 h-2.5 bg-gray-700'
                  }`}
                />
              );
            })}
          </div>
          {/* Encouragement message */}
          {encouragement && (
            <motion.p
              key={step}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-[#00D47E] text-sm font-semibold mt-3"
            >
              ✨ {encouragement}
            </motion.p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-6 py-6 md:py-8">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl"
            >

              {step === 1 && (
                <StepLoanType formData={formData} handleChange={handleChange} />
              )}

              {step === 2 && (
                <StepCalculator
                  formData={formData}
                  handleChange={handleChange}
                  loanAmount={loanAmount}
                  formatCurrency={formatCurrency}
                />
              )}

              {step === 3 && (
                <StepPersonalDetails formData={formData} handleChange={handleChange} />
              )}

              {step === 4 && (
                <StepAddress formData={formData} handleChange={handleChange} />
              )}

              {step === 5 && (
                <StepEmployment formData={formData} handleChange={handleChange} />
              )}

              {step === 6 && (
                <StepFinalReview
                  applicationId={applicationId}
                  approvalResult={approvalResult}
                  contactSubmitted={contactSubmitted}
                  contactSubmitting={contactSubmitting}
                  formData={formData}
                  formatCurrency={formatCurrency}
                  handleAssetChange={handleAssetChange}
                  handleChange={handleChange}
                  handleLiabilityChange={handleLiabilityChange}
                  handleNoOtherDebtsChange={handleNoOtherDebtsChange}
                  handleSpeakWithUs={handleSpeakWithUs}
                  handleSubmitApplication={handleSubmitApplication}
                  loading={loading}
                  loanAmount={loanAmount}
                  onReturnHome={returnHome}
                  scoringError={scoringError}
                  scoringErrorMessage={scoringErrorMessage}
                  scoringInProgress={scoringInProgress}
                  setShowSpeakWithUs={setShowSpeakWithUs}
                  showSpeakWithUs={showSpeakWithUs}
                />
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-4 mt-8 pt-6 border-t border-gray-100">
                {step > 1 && !approvalResult && !contactSubmitted && !loading && !scoringInProgress && (
                  <Button variant="outline" onClick={handleBack} className="flex-1" data-testid="back-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
                {step < totalSteps && (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className="flex-1 bg-black text-white hover:bg-gray-900"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
                {step === totalSteps && !approvalResult && !contactSubmitted && !scoringInProgress && !scoringError && (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading || scoringInProgress}
                    className="flex-1 bg-[#00D47E] text-black hover:bg-[#00D47E]/90"
                    data-testid="check-eligibility-btn"
                  >
                    {loading || scoringInProgress ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        Check My Eligibility
                        <Zap className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
